/**
 * The drawings the demo entrypoint can run, in the order they are shown
 * (task brief, not alphabetical) — the same list behind the case picker in
 * `features/demo`; the Eingang's own dropdown is gone with its screen.
 *
 * Lives here rather than in either feature: a feature may not import
 * another feature's module (`import/no-restricted-paths`, R-W1 in
 * `eslint.config.js`), and this list is exactly the kind of shared building
 * block that rule sends to `src/components` (R-W2) — even though it draws
 * nothing itself.
 */
export interface DemoCase {
  id: string;
  label: string;
  /**
   * Whether "Lauf starten" can reach the model for this case.
   *
   * `false` where the case register behind `demo.py` carries no drawing: a
   * live run then fails with HTTP 409 `case_not_runnable` — "carries no files
   * in the register" — and only the recorded run works. The screen reads this
   * property instead of re-deriving the rule, so the knowledge stays in one
   * place rather than being copied into a component and drifting.
   */
  liveRunnable: boolean;
}

/**
 * Ids beginning with `SYN-` are the synthetic corpus: an expected extraction
 * in `apps/api/tests/fixtures/synthetic`, with no drawing behind it. That is
 * the whole rule for "can this run live", and it is written down once, here.
 */
const SYNTHETIC_PREFIX = "SYN-";

const CASES: Omit<DemoCase, "liveRunnable">[] = [
  // L1 steht vorn: eine echte Zeichnung mit aufgezeichnetem Lauf, also beide
  // Wege — der aufgezeichnete Lauf zeigt die Antwort des Anbieters vom
  // 14.08.2026 immer gleich, der Live-Lauf liest sie in etwa 45 Sekunden neu.
  // Darunter die beiden Kontur-Fälle aus dem Prüfkorpus: kein Modellaufruf,
  // kein Netz, bei jedem Klick dasselbe Bild — dafür ohne Zeichnung und
  // deshalb nur als Aufzeichnung. Die neun Fälle darunter spielen
  // Aufzeichnungen vom 13.08.2026 ab, die noch die Richtungsfolge von
  // `kontur-a-v0.2` tragen und deshalb keine Kontur mehr ergeben — sie zeigen
  // die Auslese, nicht den Umriss.
  { id: "L1", label: "CAD-Rechteck, beide Breiten angeschrieben" },
  { id: "SYN-KONT-03", label: "Rechteck-Umriss, beide Breiten angeschrieben" },
  { id: "SYN-KONT-01", label: "L-Lauf, eine Wand ohne Maß" },
  { id: "A-9", label: "Bleistift-Handskizze, L-Lauf (mm)" },
  { id: "A-2", label: "Fassadenaufriss mit Referenzliste" },
  { id: "A-3", label: "Hausumlauf, drei Teilzüge" },
  { id: "A-6", label: "Lageplan mit Handbemaßung (erfasst)" },
  { id: "A-8", label: "Vektor-CAD Terrasse (cm)" },
  { id: "ST-1", label: "Gerader Lauf mit Säulen" },
  { id: "ST-2", label: "U-Lauf, gemischte Einheiten" },
  { id: "ST-3", label: "U-Form Punktraster (erfasst)" },
  { id: "B-2", label: "Regeldetail — Abweisung" },
];

export const DEMO_CASES: DemoCase[] = CASES.map((demoCase) => ({
  ...demoCase,
  liveRunnable: !demoCase.id.startsWith(SYNTHETIC_PREFIX),
}));
